import {t} from '@lingui/core/macro';

import {IUserOutput} from 'entities/user';
import {TariffBooleanLimit} from 'entities/tariff';

export type DomainChecksFormType = {
    ssl_watch: boolean,
    domain_watch: boolean,
    ssl_redirect_watch: boolean,
    robots_txt_watch: boolean,
    rkn_watch: boolean,
};

export type DomainCheckKey = keyof DomainChecksFormType;

export interface IDomainCheckFeature {
    name: DomainCheckKey,
    condition: TariffBooleanLimit,
    label: string,
    help: string,
}

interface IDomainCheckDescriptor {
    condition: TariffBooleanLimit,
    label: () => string,
    help: () => string,
}

const DOMAIN_CHECK_DESCRIPTORS: Record<DomainCheckKey, IDomainCheckDescriptor> = {
    ssl_watch: {
        condition: 'monitor_ssl_enabled',
        label: () => t`Мониторинг SSL`,
        help: () => t`Проверяет срок действия SSL-сертификата и уведомляет за 10, 5 и 1 день до его окончания.`,
    },
    domain_watch: {
        condition: 'monitor_domain_watch_enabled',
        label: () => t`Мониторинг срока домена`,
        help: () => t`Проверяет срок действия домена и уведомляет за 30, 14, 7 и 1 день до окончания регистрации.`,
    },
    ssl_redirect_watch: {
        condition: 'monitor_ssl_redirect_watch_enabled',
        label: () => t`Мониторинг редиректа с http на https`,
        // eslint-disable-next-line max-len
        help: () => t`Проверяет наличие перенаправления с http на https и сообщает об ошибке, если редирект не настроен.`,
    },
    robots_txt_watch: {
        condition: 'monitor_robots_txt_watch_enabled',
        label: () => t`Мониторинг файла robots.txt`,
        // eslint-disable-next-line max-len
        help: () => t`Проверяет наличие и доступность robots.txt, индексацию главной страницы и наличие ссылки на sitemap.`,
    },
    rkn_watch: {
        condition: 'monitor_rkn_watch_enabled',
        label: () => t`Мониторинг РКН`,
        help: () => t`Проверяет попадание домена в реестр РКН и сигнализирует о проблеме при наличии блокировки.`,
    },
};

export const DOMAIN_CHECK_KEYS: DomainCheckKey[] = [
    'ssl_watch',
    'domain_watch',
    'ssl_redirect_watch',
    'robots_txt_watch',
    'rkn_watch',
];

export const HTTPS_DEPENDENT_CHECK_KEYS: DomainCheckKey[] = ['ssl_watch', 'ssl_redirect_watch'];

export function getDomainCheckCondition(key: DomainCheckKey): TariffBooleanLimit {
    return DOMAIN_CHECK_DESCRIPTORS[key].condition;
}

export function getDomainCheckLabel(key: DomainCheckKey): string {
    return DOMAIN_CHECK_DESCRIPTORS[key].label();
}

export function getDomainCheckFeatures(): IDomainCheckFeature[] {
    return DOMAIN_CHECK_KEYS.map(name => ({
        name,
        condition: DOMAIN_CHECK_DESCRIPTORS[name].condition,
        label: DOMAIN_CHECK_DESCRIPTORS[name].label(),
        help: DOMAIN_CHECK_DESCRIPTORS[name].help(),
    }));
}

export function getDomainChecksDefaults(user: IUserOutput | null): DomainChecksFormType {
    return {
        ssl_watch: !!user?.tariff.monitor_ssl_enabled,
        domain_watch: !!user?.tariff.monitor_domain_watch_enabled,
        ssl_redirect_watch: !!user?.tariff.monitor_ssl_redirect_watch_enabled,
        robots_txt_watch: !!user?.tariff.monitor_robots_txt_watch_enabled,
        rkn_watch: false,
    };
}
